import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { theme } from '../theme';
import { api } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Settings2, Building2, Share2, CreditCard, Mic, KeyRound, Globe, Mail } from 'lucide-react';

const tabs = [
  { id: 'Profile' as const, icon: Settings2 },
  { id: 'Business' as const, icon: Building2 },
  { id: 'Social Accounts' as const, icon: Share2 },
  { id: 'Integrations' as const, icon: Globe },
  { id: 'Email' as const, icon: Mail },
  { id: 'Subscription' as const, icon: CreditCard },
  { id: 'Voice Clone' as const, icon: Mic },
  { id: 'API Keys' as const, icon: KeyRound },
];

type Business = {
  id: string;
  business_name: string;
  business_type: string | null;
  city: string | null;
  state: string | null;
  location: string | null;
  description: string | null;
  logo_url: string | null;
  primary_color: string | null;
  tagline: string | null;
  target_audience: string | null;
};

type Plan = {
  id: string;
  name: string;
  price_monthly: string;
  agents_count: number;
};

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState<(typeof tabs)[number]['id']>('Profile');
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<Business | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planName, setPlanName] = useState<string | null>(null);
  const [bizForm, setBizForm] = useState<Partial<Business>>({});
  const [saving, setSaving] = useState(false);
  const [voiceStatus] = useState<'Not setup' | 'Recording' | 'Ready'>('Not setup');

  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [wantsChangeEmail, setWantsChangeEmail] = useState(false);

  const [editingProfileEmail, setEditingProfileEmail] = useState(false);
  const [profileEmailDraft, setProfileEmailDraft] = useState('');
  const [profileEmailSaving, setProfileEmailSaving] = useState(false);
  const [profileEmailErr, setProfileEmailErr] = useState<string | null>(null);

  const hasNotificationEmail =
    Boolean(user?.email?.trim()) &&
    !String(user?.email).toLowerCase().endsWith('@odc.phone');

  const readEmailPref = (k: string, def = true) => {
    const v = localStorage.getItem(k);
    if (v === null) return def;
    return v === 'true';
  };

  const [emailPrefs, setEmailPrefs] = useState(() => ({
    email_new_lead: readEmailPref('email_new_lead'),
    email_daily_summary: readEmailPref('email_daily_summary'),
    email_agent_reports: readEmailPref('email_agent_reports'),
  }));

  /** From GET /email/status — button shows when canSendTestEmail (Resend or local simulate). */
  const [emailStatus, setEmailStatus] = useState<{
    emailConfigured: boolean;
    canSendTestEmail: boolean;
  } | null>(null);

  /** Inline under test button (sent / error only). */
  const [emailTestLine, setEmailTestLine] = useState<{
    kind: 'sent' | 'err';
    text: string;
  } | null>(null);

  const setEmailPref = (key: keyof typeof emailPrefs, on: boolean) => {
    localStorage.setItem(key, String(on));
    setEmailPrefs((p) => ({ ...p, [key]: on }));
  };

  const load = useCallback(async () => {
    const [b, pl] = await Promise.all([
      api.get<{ business: Business | null }>('/business/me'),
      api.get<{ plans: Plan[] }>('/plans'),
    ]);
    setBusiness(b.data.business);
    if (b.data.business) {
      setBizForm(b.data.business);
    }
    setPlans(pl.data.plans);
    try {
      const st = await api.get<{ stats: { subscription_plan_name: string | null } }>('/dashboard/stats');
      setPlanName(st.data.stats.subscription_plan_name);
    } catch {
      setPlanName(null);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  useEffect(() => {
    if (tab !== 'Profile') {
      setEditingProfileEmail(false);
      setProfileEmailDraft('');
      setProfileEmailErr(null);
    }
    if (tab !== 'Email') {
      setEmailTestLine(null);
      setEmailStatus(null);
      setWantsChangeEmail(false);
      setNewEmail('');
      setEmailErr(null);
      return;
    }
    let cancelled = false;
    void api
      .get<{ emailConfigured: boolean; canSendTestEmail: boolean }>('/email/status')
      .then((r) => {
        if (!cancelled) {
          setEmailStatus({
            emailConfigured: Boolean(r.data.emailConfigured),
            canSendTestEmail: Boolean(r.data.canSendTestEmail),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEmailStatus({ emailConfigured: false, canSendTestEmail: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  async function saveProfileEmail() {
    setEmailErr(null);
    const trimmed = newEmail.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setEmailErr('Enter a valid email address.');
      return;
    }
    setEmailSaving(true);
    try {
      await api.put<{ ok: boolean; message?: string }>('/auth/profile/email', {
        email: trimmed,
      });
      await refreshUser();
      setWantsChangeEmail(false);
      setNewEmail('');
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const msg = (e.response?.data as { error?: string } | undefined)?.error;
        setEmailErr(msg || e.message || 'Could not save email.');
      } else {
        setEmailErr('Could not save email.');
      }
    } finally {
      setEmailSaving(false);
    }
  }

  async function saveProfileTabEmail() {
    setProfileEmailErr(null);
    const trimmed = profileEmailDraft.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setProfileEmailErr('Enter a valid email address.');
      return;
    }
    setProfileEmailSaving(true);
    try {
      try {
        await api.put<{ ok: boolean; message?: string; user?: unknown }>('/auth/update-email', {
          email: trimmed,
        });
      } catch (first: unknown) {
        if (axios.isAxiosError(first) && first.response?.status === 404) {
          await api.put<{ ok: boolean; message?: string; user?: unknown }>('/auth/profile/email', {
            email: trimmed,
          });
        } else {
          throw first;
        }
      }
      await refreshUser();
      setEditingProfileEmail(false);
      setProfileEmailDraft('');
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (!e.response) {
          setProfileEmailErr(
            `Cannot reach API (${String(api.defaults.baseURL)}). Check connection, or Railway CORS: FRONTEND_URL must include https://onlinedigitalcares.com (and www if used). ${e.message}`
          );
        } else {
          const msg = (e.response.data as { error?: string } | undefined)?.error;
          setProfileEmailErr(msg || e.message || 'Could not save email.');
        }
      } else {
        setProfileEmailErr('Could not save email.');
      }
    } finally {
      setProfileEmailSaving(false);
    }
  }

  function beginProfileEmailEdit() {
    setEditingProfileEmail(true);
    setProfileEmailErr(null);
    const em = user?.email ?? '';
    const synthetic = !em || em.toLowerCase().endsWith('@odc.phone');
    setProfileEmailDraft(synthetic ? '' : em);
  }

  async function saveBusiness() {
    if (!business) return;
    setSaving(true);
    try {
      await api.put(`/business/${business.id}`, {
        business_name: bizForm.business_name,
        business_type: bizForm.business_type,
        city: bizForm.city,
        state: bizForm.state,
        location: bizForm.location,
        description: bizForm.description,
        logo_url: bizForm.logo_url,
        primary_color: bizForm.primary_color,
        tagline: bizForm.tagline,
        target_audience: bizForm.target_audience,
      });
      await load();
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  const socialPlatforms = [
    { id: 'facebook', name: 'Facebook', logo: '📘' },
    { id: 'instagram', name: 'Instagram', logo: '📸' },
    { id: 'youtube', name: 'YouTube', logo: '▶️' },
    { id: 'linkedin', name: 'LinkedIn', logo: '💼' },
    { id: 'whatsapp', name: 'WhatsApp', logo: '💬' },
  ];

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header>
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.secondary }}>
            Control center
          </span>
        </div>
        <h1 className="mt-3 text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
          Settings
        </h1>
        <p className="mt-1 max-w-xl text-sm" style={{ color: 'var(--muted)' }}>
          Profile, business DNA, integrations, billing, and developer keys — unified ODC styling.
        </p>
      </header>

      <div className="odc-card !p-2 sm:!p-3" style={{ transform: 'none' }}>
        <div className="flex flex-wrap gap-1 border-b pb-2 sm:gap-2" style={{ borderColor: 'var(--border)' }}>
          {tabs.map(({ id, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                  active ? 'odc-pill-tab active' : ''
                }`}
                style={
                  active
                    ? {
                        borderColor: theme.primary,
                        background: `${theme.primary}20`,
                        color: 'var(--text)',
                      }
                    : { color: 'var(--muted)' }
                }
                onClick={() => setTab(id)}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-80" />
                {id}
              </button>
            );
          })}
        </div>

        <div className="p-4 sm:p-6">
          {tab === 'Profile' && (
            <div className="space-y-6">
              <div
                className="flex flex-wrap items-center gap-4 rounded-2xl border p-4 sm:p-5"
                style={{ borderColor: 'var(--border)', background: 'var(--bg2)' }}
              >
                <div
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {(user?.name || user?.mobile || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                    {user?.name || 'Account'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    Signed in · refresh syncs with the server
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={editingProfileEmail}
                    className="rounded-lg border px-3 py-2 text-xs font-bold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                    style={{
                      borderColor: 'rgba(190,81,3,0.35)',
                      background: 'rgba(190,81,3,0.1)',
                      color: '#E8721A',
                    }}
                    onClick={beginProfileEmailEdit}
                  >
                    Edit email
                  </button>
                  <button type="button" className="odc-btn-ghost !py-2.5 text-xs sm:text-sm" onClick={() => void refreshUser()}>
                    Refresh profile
                  </button>
                </div>
              </div>
              <dl className="grid gap-4 sm:grid-cols-2">
                <div className="odc-card !p-4" style={{ transform: 'none', background: 'var(--bg2)' }}>
                  <dt className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Name
                  </dt>
                  <dd className="mt-1 text-sm font-medium" style={{ color: 'var(--text)' }}>
                    {user?.name}
                  </dd>
                </div>
                <div className="odc-card !p-4" style={{ transform: 'none', background: 'var(--bg2)' }}>
                  <dt className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Mobile
                  </dt>
                  <dd className="mt-1 text-sm font-medium" style={{ color: 'var(--text)' }}>
                    {user?.mobile}
                  </dd>
                </div>
                <div className="odc-card !p-4 sm:col-span-2" style={{ transform: 'none', background: 'var(--bg2)' }}>
                  <dt className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Email
                  </dt>
                  <dd className="mt-2">
                    {!editingProfileEmail ? (
                      <div className="flex flex-wrap items-center gap-3">
                        <span
                          className="text-sm font-medium"
                          style={{
                            color:
                              user?.email?.toLowerCase().endsWith('@odc.phone') || !user?.email
                                ? 'var(--muted)'
                                : 'var(--text)',
                          }}
                        >
                          {user?.email?.toLowerCase().endsWith('@odc.phone') || !user?.email
                            ? 'No email set'
                            : user.email}
                        </span>
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-bold transition hover:opacity-90"
                          style={{
                            borderColor: 'rgba(190,81,3,0.35)',
                            background: 'rgba(190,81,3,0.1)',
                            color: '#E8721A',
                          }}
                          onClick={beginProfileEmailEdit}
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <input
                          className="odc-input max-w-full flex-1 !py-2.5 text-sm sm:max-w-xs"
                          type="email"
                          placeholder="your@email.com"
                          value={profileEmailDraft}
                          onChange={(e) => setProfileEmailDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void saveProfileTabEmail();
                            }
                          }}
                          autoFocus
                          autoComplete="email"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={profileEmailSaving}
                            className="odc-btn-primary !py-2.5 !text-sm disabled:opacity-60"
                            onClick={() => void saveProfileTabEmail()}
                          >
                            {profileEmailSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            disabled={profileEmailSaving}
                            className="odc-btn-ghost !py-2.5 !text-sm disabled:opacity-60"
                            onClick={() => {
                              setEditingProfileEmail(false);
                              setProfileEmailDraft('');
                              setProfileEmailErr(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {profileEmailErr ? (
                      <p className="mt-2 text-sm" style={{ color: theme.danger }}>
                        {profileEmailErr}
                      </p>
                    ) : null}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {tab === 'Business' && business && (
            <div className="space-y-6">
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                These fields power agent context and onboarding quality.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {(
                  [
                    ['business_name', 'Business name'],
                    ['business_type', 'Type'],
                    ['city', 'City'],
                    ['state', 'State'],
                    ['location', 'Location / area'],
                  ] as const
                ).map(([k, label]) => (
                  <label key={k} className="block text-sm">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                      {label}
                    </span>
                    <input
                      className="odc-input mt-1.5 !py-2.5 text-sm"
                      value={(bizForm[k] as string) || ''}
                      onChange={(e) => setBizForm({ ...bizForm, [k]: e.target.value })}
                    />
                  </label>
                ))}
                <label className="sm:col-span-2 block text-sm">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Description
                  </span>
                  <textarea
                    className="odc-input mt-1.5 min-h-[5rem] resize-y text-sm"
                    rows={3}
                    value={bizForm.description || ''}
                    onChange={(e) => setBizForm({ ...bizForm, description: e.target.value })}
                  />
                </label>
                <label className="sm:col-span-2 block text-sm">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Tagline
                  </span>
                  <input
                    className="odc-input mt-1.5 !py-2.5 text-sm"
                    value={bizForm.tagline || ''}
                    onChange={(e) => setBizForm({ ...bizForm, tagline: e.target.value })}
                  />
                </label>
                <label className="sm:col-span-2 block text-sm">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    Target audience
                  </span>
                  <textarea
                    className="odc-input mt-1.5 min-h-[4rem] resize-y text-sm"
                    rows={2}
                    value={bizForm.target_audience || ''}
                    onChange={(e) => setBizForm({ ...bizForm, target_audience: e.target.value })}
                  />
                </label>
                <div className="sm:col-span-2">
                  <button
                    type="button"
                    disabled={saving}
                    className="odc-btn-primary disabled:opacity-60"
                    onClick={() => void saveBusiness()}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'Business' && !business && (
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              No business on file. Complete onboarding first.
            </p>
          )}

          {tab === 'Email' && (
            <div className="space-y-6">
              <div
                className="odc-card !rounded-xl !p-4 sm:!p-5"
                style={{ transform: 'none', background: 'var(--bg2)' }}
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: 'var(--muted)' }}
                >
                  Account notification email
                </p>
                {hasNotificationEmail && !wantsChangeEmail ? (
                  <div className="mt-3">
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Notification email:{' '}
                      <strong style={{ color: 'var(--text)' }}>{user?.email}</strong>
                    </p>
                    <button
                      type="button"
                      className="mt-3 text-xs font-semibold underline-offset-2 transition hover:opacity-90"
                      style={{ color: theme.primary }}
                      onClick={() => {
                        setWantsChangeEmail(true);
                        setNewEmail(user?.email ?? '');
                        setEmailErr(null);
                      }}
                    >
                      Change email
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 space-y-3">
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      {hasNotificationEmail
                        ? 'Update the address we use for notifications and test email:'
                        : 'Add your email to receive notifications and test delivery:'}
                    </p>
                    <input
                      className="odc-input w-full max-w-md !py-2.5 text-sm"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void saveProfileEmail();
                        }
                      }}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={emailSaving}
                        className="odc-btn-primary !py-2.5 !text-sm disabled:opacity-60"
                        onClick={() => void saveProfileEmail()}
                      >
                        {emailSaving ? 'Saving…' : 'Save email'}
                      </button>
                      {hasNotificationEmail && wantsChangeEmail ? (
                        <button
                          type="button"
                          disabled={emailSaving}
                          className="odc-btn-primary !border !border-[var(--border)] !bg-transparent !text-sm !text-[var(--text)] disabled:opacity-60"
                          style={{ boxShadow: 'none' }}
                          onClick={() => {
                            setWantsChangeEmail(false);
                            setNewEmail('');
                            setEmailErr(null);
                          }}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                {emailErr ? (
                  <p className="mt-3 text-sm" style={{ color: theme.danger }}>
                    {emailErr}
                  </p>
                ) : null}
              </div>

              <div>
                <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>
                  📧 Email Notifications
                </h3>
                <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
                  These toggles are saved on this device only.
                </p>
              </div>
              <ul className="space-y-3">
                {(
                  [
                    {
                      key: 'email_new_lead' as const,
                      label: 'New Lead Alerts',
                      description: 'Get email when a new lead comes in',
                    },
                    {
                      key: 'email_daily_summary' as const,
                      label: 'Daily Summary',
                      description: 'Daily report of your business activity',
                    },
                    {
                      key: 'email_agent_reports' as const,
                      label: 'Agent Reports',
                      description: 'Weekly AI agent performance report',
                    },
                  ] as const
                ).map((item) => (
                  <li
                    key={item.key}
                    className="odc-card flex flex-wrap items-center justify-between gap-4 !rounded-xl !p-4 sm:!p-5"
                    style={{ transform: 'none', background: 'var(--bg2)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-bold" style={{ color: 'var(--text)' }}>
                        {item.label}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
                        {item.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={emailPrefs[item.key]}
                      className={`odc-toggle shrink-0 ${emailPrefs[item.key] ? 'active' : ''}`}
                      onClick={() => setEmailPref(item.key, !emailPrefs[item.key])}
                    />
                  </li>
                ))}
              </ul>
              {emailStatus?.canSendTestEmail && !emailStatus.emailConfigured ? (
                <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
                  Local dev: click runs a safe simulation (no inbox). Real mail after Resend on the server.
                </p>
              ) : null}
              {emailStatus?.canSendTestEmail ? (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="odc-btn-primary !text-sm"
                      onClick={() => {
                        setEmailTestLine(null);
                        void api
                          .post('/email/test')
                          .then((res) => {
                            const d = res.data as {
                              sent?: boolean;
                              simulated?: boolean;
                              message?: string;
                            };
                            if (d?.sent) {
                              setEmailTestLine({
                                kind: 'sent',
                                text: d.simulated
                                  ? 'Local test OK (simulated — no mail to inbox). See API terminal for details.'
                                  : 'Test email sent — check inbox and spam.',
                              });
                            } else {
                              setEmailTestLine({
                                kind: 'err',
                                text: d?.message || 'Could not send test email.',
                              });
                            }
                          })
                          .catch((e: unknown) => {
                            const body = (e as { response?: { data?: { error?: string; message?: string } } })
                              ?.response?.data;
                            const m = body?.message || body?.error;
                            setEmailTestLine({
                              kind: 'err',
                              text: m || 'Could not send test email.',
                            });
                          });
                      }}
                    >
                      Send Test Email
                    </button>
                  </div>
                  {emailTestLine ? (
                    <p
                      className="text-sm"
                      style={{
                        color:
                          emailTestLine.kind === 'err' ? theme.danger : theme.success,
                      }}
                    >
                      {emailTestLine.text}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          {tab === 'Integrations' && (
            <ul className="grid gap-4 sm:grid-cols-2">
              {(
                [
                  {
                    logo: '💬',
                    name: 'WhatsApp Business API',
                    desc: 'Send automated messages, orders & broadcasts',
                    status: 'Not Connected',
                    primary: true as const,
                    action: 'Connect',
                  },
                  {
                    logo: '🎙️',
                    name: 'ElevenLabs Voice Clone',
                    desc: 'Clone your voice for AI calling',
                    status: 'Not Connected',
                    primary: true as const,
                    action: 'Setup',
                  },
                  {
                    logo: '📍',
                    name: 'Google My Business',
                    desc: 'Auto-manage reviews, posts & Q&A',
                    status: 'Not Connected',
                    primary: false as const,
                    action: 'Connect',
                  },
                  {
                    logo: '📱',
                    name: 'Meta Business Suite',
                    desc: 'Facebook + Instagram automation',
                    status: 'Not Connected',
                    primary: false as const,
                    action: 'Connect',
                  },
                ] as const
              ).map((int) => (
                <li
                  key={int.name}
                  className="odc-card flex flex-col gap-3 !rounded-xl !p-4 sm:!p-5"
                  style={{ transform: 'none', background: 'var(--bg2)' }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl leading-none">{int.logo}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold" style={{ color: 'var(--text)' }}>
                        {int.name}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
                        {int.desc}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3" style={{ borderColor: 'var(--border)' }}>
                    <span className="odc-badge odc-badge-amber text-[10px]">{int.status}</span>
                    <button
                      type="button"
                      className={int.primary ? 'odc-btn-primary !py-2 !text-xs' : 'odc-btn-ghost !py-2 !text-xs'}
                      onClick={() => alert(`${int.action} ${int.name} (demo)`)}
                    >
                      {int.action}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {tab === 'Social Accounts' && (
            <ul className="space-y-3">
              {socialPlatforms.map((p) => (
                <li
                  key={p.id}
                  className="odc-card flex flex-wrap items-center justify-between gap-4 !rounded-xl !p-4"
                  style={{ transform: 'none', background: 'var(--bg2)' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{p.logo}</span>
                    <div>
                      <p className="font-bold" style={{ color: 'var(--text)' }}>
                        {p.name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>
                        OAuth in production — demo connect below
                      </p>
                    </div>
                  </div>
                  <button type="button" className="odc-btn-primary !text-xs" onClick={() => alert(`Connect ${p.name} (demo)`)}>
                    Connect
                  </button>
                </li>
              ))}
            </ul>
          )}

          {tab === 'Subscription' && (
            <div className="space-y-6">
              <div
                className="rounded-2xl border p-5 sm:p-6"
                style={{
                  borderColor: 'rgba(16,185,129,0.42)',
                  background: `linear-gradient(135deg, ${theme.primary}14, var(--bg2))`,
                }}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.primary }}>
                  Billing
                </p>
                <p className="mt-2 text-lg font-bold" style={{ color: 'var(--text)' }}>
                  Current plan
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="odc-badge odc-badge-accent text-sm">{planName || 'None'}</span>
                  {!planName ? (
                    <span className="text-sm" style={{ color: 'var(--muted)' }}>
                      Upgrade anytime — pick a tier below.
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {plans.map((p) => {
                  const isCurrent = planName && p.name === planName;
                  return (
                    <div
                      key={p.id}
                      className="odc-card relative overflow-hidden !rounded-xl !p-5"
                      style={{
                        transform: 'none',
                        background: 'var(--bg2)',
                        borderColor: isCurrent ? 'rgba(16,185,129,0.5)' : 'var(--border)',
                        boxShadow: isCurrent ? '0 0 0 1px rgba(16,185,129,0.28)' : undefined,
                      }}
                    >
                      {isCurrent ? (
                        <span className="absolute right-3 top-3 odc-badge odc-badge-green text-[9px]">Current</span>
                      ) : null}
                      <h3 className="font-bold" style={{ color: 'var(--text)' }}>
                        {p.name}
                      </h3>
                      <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
                        <span className="text-2xl font-extrabold" style={{ color: theme.primary, fontFamily: "'Space Grotesk',sans-serif" }}>
                          ₹{Number(p.price_monthly).toLocaleString('en-IN')}
                        </span>
                        <span className="text-[10px] font-medium"> /mo</span>
                        <br />
                        AI agents included
                      </p>
                      <button
                        type="button"
                        className="odc-btn-primary mt-4 w-full !py-2.5 !text-xs"
                        disabled={!!isCurrent}
                        onClick={() => {
                          if (!business) {
                            alert('Create a business first');
                            return;
                          }
                          void (async () => {
                            await api.post('/plans/subscribe', {
                              businessId: business.id,
                              planId: p.id,
                              billingCycle: 'monthly',
                            });
                            alert('Plan updated (demo)');
                            await load();
                          })();
                        }}
                      >
                        {isCurrent ? 'Active plan' : 'Upgrade / Switch'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'Voice Clone' && (
            <div className="odc-card !rounded-xl !p-5 sm:!p-6" style={{ transform: 'none', background: 'var(--bg2)' }}>
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>
                ElevenLabs voice clone
              </h3>
              <p className="mt-3 text-sm" style={{ color: 'var(--muted)' }}>
                Status: <span className="odc-badge odc-badge-accent">{voiceStatus}</span>
              </p>
              <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                <li>Record 3 short samples in a quiet room.</li>
                <li>We train a private voice model for AI Sales Person.</li>
                <li>Approve test calls before going live.</li>
              </ol>
            </div>
          )}

          {tab === 'API Keys' && (
            <div className="space-y-4">
              {[
                ['ODC_PUBLISHABLE_KEY', 'pk_live_••••••••8f3a'],
                ['ODC_VOICE_SECRET', 'vc_sk_••••••••91ab'],
                ['META_ADS_TOKEN', 'ea_••••••••••••2c'],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="odc-card flex flex-wrap items-center justify-between gap-3 !rounded-xl !p-4 font-mono text-xs sm:text-sm"
                  style={{ transform: 'none', background: 'var(--bg2)' }}
                >
                  <span style={{ color: 'var(--muted)' }}>{label}</span>
                  <span className="odc-badge odc-badge-amber font-mono text-[10px]">{val}</span>
                </div>
              ))}
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Keys are masked. Rotate from the developer console when wired.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
